import { bindExecutionDelete } from './runs';
import type { PageContext, PageDefinition } from '../lib/types';
import { html } from '../lib/dom';
import { icon } from '../components/icons';
import { coverEditor, bindCoverEditors } from '../components/entity-cover';
import { tabs, favButton, status, kpi, kv, empty } from '../components/ui';
import { getFlow, flowGraph } from '../api/flows';
import { getProject, getWorkspace, getUser } from '../api/workspace';
import { listRuns, runCounts } from '../api/runs';
import { runsTable, runsMore, runsEmpty, bindRunsMore } from './runs';
import { dateTime } from '../lib/format';

export function render(ctx: PageContext): PageDefinition {
  const flow = getFlow(ctx.params.flowId);
  const project = flow && getProject(flow.project_id);
  if (!flow || !project || project.id !== ctx.params.projectId) return {
    title: 'Not found', body: html`<div class="page">${empty('Flow not found')}<a href="#/flows">Back to flows</a></div>`,
  };
  const workspace = getWorkspace(project.workspace_id);
  const canvas = `#/projects/${project.id}/flows/${flow.id}`;
  const tab = ctx.query.tab === 'executions' ? 'executions' : 'overview';
  const filters = { projectId: project.id, flowId: flow.id, ...(tab === 'overview' ? { limit: 5 } : {}) };
  const rows = listRuns(filters);
  const counts = runCounts({ projectId: project.id, flowId: flow.id });
  const total = Object.values(counts).reduce((sum: number, value: any) => sum + Number(value), 0);
  const graph = flowGraph(flow.id);
  const nodes = flow.graph?.nodes?.length ?? flow.node_count ?? graph.nodes.length;
  const creator = getUser(flow.created_by);
  return {
    title: flow.name,
    body: html`<div class="page">
      <a data-back class="btn ghost sm" href="${canvas}" style="align-self:flex-start">${icon('back')}Back</a>
      <header class="page-head">
        <div class="row" style="gap:16px;align-items:center">
          ${coverEditor(flow, 'flow', 'cover-editor-compact')}
          <div><div class="eyebrow">Flow · ${workspace?.name || ''} · ${project.name}</div>
            <div class="row" style="gap:6px"><h1>${flow.name}</h1>${favButton('flow', flow.id)}</div>
            ${flow.description ? html`<p class="lede" style="margin-top:4px">${flow.description}</p>` : ''}
          </div>
        </div>
        <div class="row wrap">${status(flow.status || 'draft')}
          <a class="btn" href="${canvas}?mode=app">${icon('play')}Use as app</a>
          <a class="btn primary" href="${canvas}">Open canvas →</a>
        </div>
      </header>
      ${tabs([['overview', 'Overview'], ['executions', 'Executions', total]], tab)}
      ${tab === 'overview' ? html`
        <div class="grid c4">${kpi('Nodes', nodes)}${kpi('Executions', total)}${kpi('Completed', counts.completed)}${kpi('Active', counts.queued + counts.running)}</div>
        <div class="split">
          <section class="section"><div class="row" style="margin-bottom:12px"><h3>Recent executions</h3><span class="spacer"></span><a class="link" href="${canvas}/details?tab=executions">All →</a></div>
            <div class="card executions-table">${runsTable(rows, { project:false, base:'#/executions/', empty:runsEmpty(filters) })}</div>
          </section>
          <section class="card pad"><h3 style="margin-bottom:12px">Flow details</h3>${kv([
            ['Project', html`<a href="#/projects/${project.id}">${project.name}</a>`],
            ['Workspace', workspace?.name || '—'],
            ['Created by', creator?.name || '—'],
            ['Created', flow.created_at ? dateTime(flow.created_at) : '—'],
            ['Updated', flow.updated_at ? dateTime(flow.updated_at) : '—'],
          ])}</section>
        </div>` : html`
        <div class="card executions-table">${runsTable(rows, { project:false, base:'#/executions/', empty:runsEmpty(filters) })}</div>${runsMore(filters)}`}
    </div>`,
    mount(root) {
      bindExecutionDelete(root, ctx.rerender);
      bindCoverEditors(root, ctx.rerender);
      if (tab === 'executions') bindRunsMore(root, filters);
    },
  };
}
